import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import { useShallowEditorActionBar } from 'components/Workspace2EditorActionBar/store/EditorActionBarStore'
import { useCustomManufacturerDeleteOption } from 'components/WorkspaceEditor/features/deleteCustomManufacturer/hooks'
import { usePillSelection } from 'components/WorkspaceEditor/features/pillSelection/hooks/usePillSelection'
import { useTreeViewStyles } from 'components/WorkspaceEditor/styles'
import type { DataNode } from 'components/WorkspaceEditor/types'
import {
    ADDITIONAL_ELEMENT_SUFFIX,
    getStatusClass,
    isManufacturerOrBODPill,
    shouldRenderAsProductType,
} from 'components/WorkspaceEditor/utils'
import { getImportTypeInfo } from 'components/WorkspaceEditor/utils/importTypeUtils'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import React, { memo, useCallback, useMemo } from 'react'
import { useOMWorkspace } from 'shared/hooks/useOMWorkspace'
import {
    AssemblyText,
    Hyperlink,
    OptionGroup,
    PlainText,
    ReplacementOption,
    SelectionOption,
    UnitsOfMeasure,
} from '.'
import {
    isKeyedContent,
    isKeyedContentForContentItem,
} from '../ContentEditor/contentUtils'
import ProductGroupIcon from '../TreeView/ProductGroupIcon'
import ProductTypeIcon from '../TreeView/ProductTypeIcon'
import { EmptyPlainTextContainer } from './EmptyPlainTextContainer'
import { getVisibilityFlags } from './nodeContentRendererUtils'
import { useCohesiveContentWrapperStyles } from './useNodeContentRendererStyles'

/** Props for rendering node item */
export interface NodeContentRendererProps {
    /** The node to render */
    node: DataNode
    /** Whether the node is editable */
    editable?: boolean
    /** Whether the node is inactive */
    inactive?: boolean
}

/**
 * Renders the node item for a tree node
 * @param {NodeContentRendererProps} NodeContentRendererProps - Props for rendering node item
 * @returns {ReactElement} Styled node item
 */
export const NodeContentRenderer = memo<NodeContentRendererProps>(
    ({ node, editable = false, inactive = false }) => {
        const {
            type,
            contentItemNodes,
            isProductGroup,
            isProductType,
            isDisabled,
            isCustomManufacturer,
        } = node
        const {
            displayContent,
            articleTypography,
            paragraphTypography,
            inactiveText,
            addedText,
            removedText,
            readOnlyCursor,
            assemblyTextField,
            inputText,
            productGroupIcon,
            productTypeIcon,
            productIconWrapper,
        } = useTreeViewStyles()
        const { cohesiveContentWrapper } = useCohesiveContentWrapperStyles()

        const { isCurrentlyOnOMWorkspace } = useOMWorkspace()

        const {
            visibilitySettingValues: {
                areTrackChangesVisible,
                isInactiveContentVisible,
                arePTLinksVisible,
            },
        } = useShallowEditorActionBar((state) => ({
            visibilitySettingValues: state.visibilitySettingValues,
        }))

        const { selectedProjectElementNode } = useShallowSelector((state) => ({
            selectedProjectElementNode: state.selectedProjectElementNode,
        }))

        const { handlePillClick, handleNewPillDelete } = usePillSelection()
        const { onDeleteOption } = useCustomManufacturerDeleteOption(node)

        // Get import type info for the selected element
        const importInfo = useMemo(
            () => getImportTypeInfo(selectedProjectElementNode),
            [selectedProjectElementNode]
        )

        // Check if manufacturer pills should be disabled based on feature flag
        // For Project Import, OM Import, and OM/MasterSpec Clone, enable pill click selection (show full list, allow interaction)
        const isManufacturerPill = isManufacturerOrBODPill(node)
        const isFeatureFlagEnabled =
            processEnv.REACT_APP_EnableDynamicManufacturerListSelection === '1'
        const shouldDisableManufacturerPills =
            isManufacturerPill &&
            isFeatureFlagEnabled &&
            !importInfo.isProjectImport &&
            !importInfo.isOMImport &&
            !importInfo.isMasterSpecClone
        const shouldApplyDynamicManufacturerSelection =
            isFeatureFlagEnabled &&
            (!!importInfo.isMasterSpecClone || !!importInfo.isOMImport)

        const plainTextClass = useMemo(
            () =>
                type === NODE_TYPES.Article
                    ? articleTypography
                    : paragraphTypography,
            [type]
        )
        const visibilityContext = useMemo(
            () => ({
                node,
                isProductType: !!isProductType,
                areTrackChangesVisible,
                isInactiveContentVisible,
            }),
            [
                node,
                isProductType,
                areTrackChangesVisible,
                isInactiveContentVisible,
            ]
        )

        // Per-item visibility, computed once and reused both to find the effective first
        // item (so the leading EmptyPlainTextContainer added below for a paragraph
        // starting with an OptionGroup attaches to the first *visible* item rather than
        // literal index 0 — a hidden item there, e.g. deleted with track changes off,
        // would otherwise leave the next, now-effectively-first item without it) and to
        // decide per-item whether to render at all in the loop below.
        const visibilityFlags = useMemo(
            () => getVisibilityFlags(contentItemNodes, visibilityContext),
            [contentItemNodes, visibilityContext]
        )
        const firstVisibleIndex = visibilityFlags.indexOf(true)

        // Show the Product Type icon only for Product Types, not user-added/pasted paragraphs.
        const rendersAsProductType = useMemo(
            () => shouldRenderAsProductType(node),
            [node]
        )

        const renderProductIcon = useCallback(
            (tooltipText?: string, isFirstTextItem?: boolean) => {
                if (!isFirstTextItem) return null
                if (isCurrentlyOnOMWorkspace && !arePTLinksVisible) return null

                if (isProductGroup) {
                    return (
                        <HoverTooltip
                            text={`Product Group: ${tooltipText}`}
                            placements={['top', 'left']}
                            className={productGroupIcon}
                        >
                            <span className={productIconWrapper}>
                                <ProductGroupIcon
                                    className={productGroupIcon}
                                />
                            </span>
                        </HoverTooltip>
                    )
                }

                if (rendersAsProductType) {
                    return (
                        <HoverTooltip
                            text={`Product Type: ${tooltipText}`}
                            placements={['top', 'left']}
                            className={productTypeIcon}
                        >
                            <span className={productIconWrapper}>
                                <ProductTypeIcon className={productTypeIcon} />
                            </span>
                        </HoverTooltip>
                    )
                }

                return null
            },
            [
                isProductGroup,
                rendersAsProductType,
                isCurrentlyOnOMWorkspace,
                arePTLinksVisible,
            ]
        )

        // Trims leading/trailing spaces from ProductType first items to fix key:colon spacing
        const trimContentItem = useCallback(
            (item: Partial<TextContentElement>, isFirstTextItem: boolean) =>
                isProductType && isFirstTextItem && item.text
                    ? { ...item, text: item.text.trim() }
                    : item,
            [isProductType]
        )
        // User-added custom MFR: exempt from parent inactive state (Bug 2684621)
        const isUserAddedCustomMfr =
            isCustomManufacturer &&
            !node.contentItemView?.isAddedInOfficeMasterDraftProject

        const omCustomManufacturer = useMemo(
            () =>
                node.isCustomManufacturer &&
                (isCurrentlyOnOMWorkspace ||
                    node?.contentItemView?.isAddedInOfficeMasterDraftProject),
            [node, isCurrentlyOnOMWorkspace]
        )

        return (
            <div
                className={clsx(cohesiveContentWrapper, displayContent)}
                data-cohesive-content="true"
            >
                {contentItemNodes.map((contentItem, index) => {
                    if (!visibilityFlags[index]) {
                        return null
                    }

                    switch (contentItem.type) {
                        case NODE_TYPES.OptionGroup:
                            // When an OptionGroup is the paragraph's first (visible) item,
                            // there's no plain-text element in front of it to click into —
                            // add an empty text container so the user can still add content
                            // before the pills.
                            return (
                                <React.Fragment key={contentItem.id}>
                                    {index === firstVisibleIndex && (
                                        <EmptyPlainTextContainer
                                            element={{
                                                id: `${contentItem.id}${ADDITIONAL_ELEMENT_SUFFIX}`,
                                                text: '',
                                                type: NODE_TYPES.Text,
                                            }}
                                            inactive={inactive}
                                            editable={editable}
                                            node={node}
                                        />
                                    )}
                                    <OptionGroup
                                        contentItem={contentItem}
                                        inactive={inactive}
                                        disabled={!!isDisabled}
                                        editable={editable}
                                        handlePillClick={handlePillClick}
                                        parentOptionGroupId={
                                            node?.contentItemView?.id ?? ''
                                        }
                                        onNewPillDelete={handleNewPillDelete}
                                    />
                                </React.Fragment>
                            )
                        case NODE_TYPES.SelectionOption:
                            return isKeyedContent(contentItemNodes) ? (
                                <PlainText
                                    contentItem={contentItem}
                                    node={node}
                                    inactive={inactive}
                                    editable={editable}
                                    classes={clsx(
                                        plainTextClass,
                                        inactive && inactiveText,
                                        !editable && readOnlyCursor
                                    )}
                                />
                            ) : (
                                <SelectionOption
                                    contentItem={contentItem}
                                    inactive={inactive}
                                    disabled={!!isDisabled}
                                    editable={editable}
                                    onSelectionOptionClick={handlePillClick}
                                />
                            )
                        case NODE_TYPES.Hyperlink:
                            return (
                                <Hyperlink
                                    contentItem={contentItem}
                                    isKeyedHyperlink={isKeyedContentForContentItem(
                                        contentItem
                                    )}
                                    node={node}
                                    inactive={inactive}
                                    disabled={!!isDisabled}
                                    editable={editable}
                                    disableCursorOnly={
                                        shouldDisableManufacturerPills
                                    }
                                />
                            )
                        case NODE_TYPES.UnitsOfMeasure:
                            return (
                                <UnitsOfMeasure
                                    contentItem={contentItem}
                                    inactive={inactive}
                                    disabled={!!isDisabled}
                                    editable={editable}
                                    parentContentItem={
                                        node.contentItemView as Partial<
                                            TextContentElement
                                        >
                                    }
                                />
                            )
                        case NODE_TYPES.ReplacementOption:
                            return (
                                <ReplacementOption
                                    contentItem={contentItem}
                                    inactive={!isUserAddedCustomMfr && inactive}
                                    disabled={!!isDisabled}
                                    editable={editable}
                                    isCustomManufacturer={isCustomManufacturer}
                                    isPillSelected={
                                        !!node.contentItemView
                                            ?.isPillSelected ||
                                        // OM Import/MS Clone: user-added custom MFRs pre-selected
                                        !!(
                                            isUserAddedCustomMfr &&
                                            shouldApplyDynamicManufacturerSelection
                                        )
                                    }
                                    enableDynamicManufacturerSelection={
                                        shouldApplyDynamicManufacturerSelection
                                    }
                                    isCustomMFR={omCustomManufacturer}
                                    node={node}
                                    onDeleteOption={onDeleteOption}
                                />
                            )
                        case NODE_TYPES.AssemblyText:
                            return (
                                <AssemblyText
                                    contentItem={contentItem}
                                    editable={editable && !inactive}
                                    classes={clsx(assemblyTextField, inputText)}
                                />
                            )
                        default:
                            // Only render the product icon for the first PT text item
                            const isFirstTextItem = index === 0
                            const statusClass = getStatusClass(
                                inactive,
                                contentItem.changeType?.toUpperCase() ?? '',
                                areTrackChangesVisible,
                                {
                                    inactiveText,
                                    addedText,
                                    removedText,
                                }
                            )
                            const item = trimContentItem(
                                contentItem,
                                isFirstTextItem
                            )

                            return (
                                <>
                                    {renderProductIcon(
                                        item.text,
                                        isFirstTextItem
                                    )}
                                    <PlainText
                                        contentItem={item}
                                        node={node}
                                        inactive={inactive}
                                        editable={editable}
                                        classes={clsx(
                                            plainTextClass,
                                            statusClass,
                                            !editable && readOnlyCursor
                                        )}
                                    />
                                </>
                            )
                    }
                })}
            </div>
        )
    }
)
